import { createContext, useContext, useEffect, useCallback, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { getPlanByProductId, type PlanKey } from "@/lib/plans";
import { logger } from "@/lib/logger";

interface SubscriptionState {
  loading: boolean;
  subscribed: boolean;
  plan: PlanKey;
  status: string | null;
  subscriptionEnd: string | null;
  trialEnd: string | null;
  cancelAtPeriodEnd: boolean;
  priceId: string | null;
  subscriptionType: string | null;
  /**
   * A leitura da assinatura FALHOU, e o que está acima é o padrão de segurança,
   * não um fato sobre a conta.
   *
   * `fetchSubscription` nunca rejeita: erro de banco e erro do Stripe caem no
   * mesmo `EMPTY`, com `plan: "free"`. Para o PORTÃO isso é o certo — errar para
   * menos custa um clique, errar para mais entrega recurso pago. Para a TELA,
   * não: sem este campo, o assinante Premium lê "Você está no plano gratuito"
   * quando o problema é a rede, e a afirmação é falsa com cara de verdade.
   *
   * É a mesma distinção do service worker: requisição que FALHOU não pode
   * chegar ao aplicativo parecendo resposta vazia.
   */
  leituraFalhou: boolean;
  refreshSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionState>({
  loading: true,
  subscribed: false,
  plan: "free",
  status: null,
  subscriptionEnd: null,
  trialEnd: null,
  cancelAtPeriodEnd: false,
  priceId: null,
  subscriptionType: null,
  leituraFalhou: false,
  refreshSubscription: async () => {},
});

interface SubData {
  subscribed: boolean;
  plan: PlanKey;
  status: string | null;
  subscriptionEnd: string | null;
  trialEnd: string | null;
  cancelAtPeriodEnd: boolean;
  priceId: string | null;
  subscriptionType: string | null;
  leituraFalhou: boolean;
}

/**
 * `past_due` ENTROU aqui em 10/09/2026, junto com a mesma mudança em
 * `SubscriptionGate` e na resolução de limites do banco.
 *
 * Ela significa "o Stripe tentou cobrar e ainda não conseguiu", e ele repete a
 * cobrança por dias antes de desistir. Enquanto estava de fora, um cartão
 * recusado uma vez fazia `subscribed` virar `false` no mesmo segundo: o cliente
 * caía para os limites do plano gratuito e via "Assinatura Inativa" sobre os
 * relatórios, ainda dentro do período que pagou.
 *
 * Quem avisa que a cobrança está pendente é `usePlan().cobrancaPendente`, lido
 * da MESMA consulta que decide o limite.
 */
const ACTIVE_STATUSES = ["active", "trialing", "past_due"];

const EMPTY: SubData = {
  subscribed: false,
  plan: "free",
  status: null,
  subscriptionEnd: null,
  trialEnd: null,
  cancelAtPeriodEnd: false,
  priceId: null,
  subscriptionType: null,
  leituraFalhou: false,
};

/** O mesmo padrão de segurança, dizendo que ele é padrão e não fato. */
const FALHOU: SubData = { ...EMPTY, leituraFalhou: true };

async function fetchSubscription(userId: string): Promise<SubData> {
  const { data, error } = await supabase
    .from("subscriptions")
    .select("*")
    .eq("user_id", userId)
    .in("status", ACTIVE_STATUSES)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (!error && data) {
    const d = data as any;
    const subType: string | null = d.subscription_type ?? null;
    let plan: PlanKey = "free";
    if (subType === "comp" || subType === "trial") {
      plan = (d.plan_key as PlanKey) ?? "free";
    } else {
      plan = data.product_id ? getPlanByProductId(data.product_id) : "free";
    }
    return {
      subscribed: true,
      plan,
      status: data.status,
      subscriptionEnd: data.current_period_end,
      trialEnd: data.trial_end,
      cancelAtPeriodEnd: data.cancel_at_period_end || false,
      priceId: data.price_id,
      subscriptionType: subType,
      leituraFalhou: false,
    };
  }

  const erroNoBanco = Boolean(error);
  if (error) logger.error("Error reading subscription from DB:", error);

  // Stripe fallback quando DB não tem registro
  try {
    const { data: sd, error: se } = await supabase.functions.invoke("check-subscription");
    if (se) throw se;
    return {
      subscribed: sd.subscribed || false,
      plan: sd.product_id ? getPlanByProductId(sd.product_id) : "free",
      status: sd.status || null,
      subscriptionEnd: sd.subscription_end || null,
      trialEnd: sd.trial_end || null,
      cancelAtPeriodEnd: sd.cancel_at_period_end || false,
      priceId: sd.price_id || null,
      subscriptionType: null,
      leituraFalhou: false,
    };
  } catch (err) {
    logger.error("Error checking subscription via Stripe:", err);
    // Os DOIS caminhos falharam. `erroNoBanco` distingue "esta conta não tem
    // assinatura" — resposta legítima, e o caso comum — de "não consegui
    // olhar". Sem ele, a tela afirma a primeira quando vale a segunda.
    return erroNoBanco ? FALHOU : EMPTY;
  }
}

// AC6 — Impact analysis:
// With 1000 active users polling every 60s → 60,000 DB queries/hour.
// Realtime as primary eliminates this load; polling fires only on fallback (CHANNEL_ERROR / TIMED_OUT).
// Estimated saving at 1,000 active users: ~59,940 queries/hour (Realtime avg connect time ~3s).

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // AC4: staleTime 30s — limits window where a cancelled subscription stays valid in the cache
  const { data, isLoading } = useQuery({
    queryKey: ["subscription-context", user?.id],
    queryFn: () => fetchSubscription(user!.id),
    enabled: !!user?.id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  // AC1-3: track Realtime connection state to gate fallback polling
  const [realtimeConnected, setRealtimeConnected] = useState(false);

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel("subscription-changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "subscriptions", filter: `user_id=eq.${user.id}` },
        () => {
          queryClient.invalidateQueries({ queryKey: ["subscription-context", user.id] });
        }
      )
      .subscribe((status) => {
        if (status === "SUBSCRIBED") setRealtimeConnected(true);
        if (status === "CHANNEL_ERROR" || status === "TIMED_OUT" || status === "CLOSED") {
          setRealtimeConnected(false);
        }
      });
    return () => {
      supabase.removeChannel(channel);
      setRealtimeConnected(false);
    };
  }, [user, queryClient]);

  // AC2: fallback polling — only active when Realtime is disconnected
  useEffect(() => {
    if (realtimeConnected || !user) return;
    const id = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ["subscription-context", user.id] });
    }, 60_000);
    return () => clearInterval(id);
  }, [realtimeConnected, user, queryClient]);

  const refreshSubscription = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["subscription-context", user?.id] });
  }, [queryClient, user?.id]);

  const sub = user ? (data ?? EMPTY) : EMPTY;

  return (
    <SubscriptionContext.Provider
      value={{
        loading: isLoading && !!user,
        subscribed: sub.subscribed,
        plan: sub.plan,
        status: sub.status,
        subscriptionEnd: sub.subscriptionEnd,
        trialEnd: sub.trialEnd,
        cancelAtPeriodEnd: sub.cancelAtPeriodEnd,
        priceId: sub.priceId,
        subscriptionType: sub.subscriptionType,
        leituraFalhou: sub.leituraFalhou,
        refreshSubscription,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export const useSubscription = () => useContext(SubscriptionContext);
